import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, type Game, type PitchCount } from '../services/api';
import { appState } from '../store/AppState';

/**
 * PublicGameView
 *
 * Minimal read-only game viewer for guest (signed-out) browsing. Hits the
 * public read endpoints (the backend agent exposes /api/games/{id} and
 * /api/games/{id}/pitches without auth) and renders:
 *
 *   - Game name, teams, date, location
 *   - Pitch counts per player (sorted by count, descending)
 *
 * Inning timeline is intentionally omitted — Cara's quoted use case is
 * "see who pitched in that game," so we keep the surface tiny. We do not
 * reuse <observer-view> because that component is hard-bound to the
 * token-based /games/observe endpoint and would require widening its
 * data-loading contract.
 */
@customElement('public-game-view')
export class PublicGameView extends LitElement {
  createRenderRoot() {
    return this;
  }

  @property({ type: String, attribute: 'game-id' })
  gameId = '';

  @state()
  private game: Game | null = null;

  @state()
  private pitches: PitchCount[] = [];

  @state()
  private loading = true;

  @state()
  private error = '';

  async connectedCallback() {
    super.connectedCallback();
    await this.loadGame();
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('gameId') && this.gameId) {
      this.loadGame();
    }
  }

  private async loadGame() {
    if (!this.gameId) {
      this.error = 'No game specified';
      this.loading = false;
      return;
    }
    this.loading = true;
    this.error = '';
    try {
      const [game, pitches] = await Promise.all([
        api.getGame(this.gameId),
        api.getGamePitches(this.gameId),
      ]);
      this.game = game;
      this.pitches = pitches ?? [];
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      this.error = message || 'Unable to load this game.';
    } finally {
      this.loading = false;
    }
  }

  private handleBack() {
    appState.setPublicGameId(null);
  }

  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  private getTotalPitches(): number {
    return this.pitches.reduce((sum, p) => sum + p.count, 0);
  }

  render() {
    return html`
      <div class="max-w-2xl mx-auto space-y-4 animate-fade-in">
        <button
          type="button"
          class="btn btn-ghost btn-sm gap-1"
          @click=${this.handleBack}
          aria-label="Back to team directory"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        ${this.renderBody()}
      </div>
    `;
  }

  private renderBody() {
    if (this.loading) {
      return html`
        <div class="flex flex-col items-center justify-center py-16 space-y-4">
          <span class="loading loading-spinner loading-lg text-primary"></span>
          <p class="text-base-content/60">Loading game...</p>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="alert alert-error shadow-lg" role="alert">
          <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 class="font-bold">Unable to View Game</h3>
            <p class="text-sm">${this.error}</p>
          </div>
        </div>
      `;
    }

    if (!this.game) {
      return html`<div class="alert alert-warning">Game data not available</div>`;
    }

    const activePitchers = this.pitches
      .filter(p => p.count > 0)
      .sort((a, b) => b.count - a.count);

    return html`
      <div class="card bg-base-100 shadow-xl">
        <div class="card-body">
          <h2 class="card-title text-2xl">${this.game.name}</h2>

          <div class="flex flex-wrap gap-4 text-sm text-base-content/70">
            ${this.game.location ? html`
              <div class="flex items-center gap-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                ${this.game.location}
              </div>
            ` : nothing}

            <div class="flex items-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              ${this.formatDate(this.game.game_date)}
            </div>
          </div>

          ${this.game.home_team_name || this.game.away_team_name ? html`
            <div class="flex items-center gap-2 mt-2 flex-wrap">
              ${this.game.home_team_name ? html`
                <span class="badge badge-primary">${this.game.home_team_name}</span>
              ` : nothing}
              ${this.game.home_team_name && this.game.away_team_name ? html`
                <span class="text-base-content/50">vs</span>
              ` : nothing}
              ${this.game.away_team_name ? html`
                <span class="badge badge-secondary">${this.game.away_team_name}</span>
              ` : nothing}
            </div>
          ` : nothing}
        </div>
      </div>

      <div class="stats stats-horizontal shadow w-full">
        <div class="stat">
          <div class="stat-title">Total Pitches</div>
          <div class="stat-value text-primary">${this.getTotalPitches().toLocaleString()}</div>
        </div>
        <div class="stat">
          <div class="stat-title">Pitchers</div>
          <div class="stat-value">${activePitchers.length}</div>
        </div>
      </div>

      <div class="card bg-base-100 shadow-xl">
        <div class="card-body">
          <h3 class="card-title text-lg">Pitch Counts</h3>

          ${activePitchers.length === 0 ? html`
            <p class="text-base-content/60 text-center py-8">No pitches recorded for this game.</p>
          ` : html`
            <div class="overflow-x-auto">
              <table class="table table-zebra">
                <thead>
                  <tr>
                    <th>Player</th>
                    <th class="text-right">Pitches</th>
                  </tr>
                </thead>
                <tbody>
                  ${activePitchers.map(p => html`
                    <tr>
                      <td>
                        <div class="flex items-center gap-2">
                          ${p.jersey_number !== undefined && p.jersey_number !== null ? html`
                            <span class="badge badge-outline badge-sm">#${p.jersey_number}</span>
                          ` : nothing}
                          <span class="font-medium">${p.player_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td class="text-right">
                        <span class="font-mono text-xl font-bold">${p.count}</span>
                      </td>
                    </tr>
                  `)}
                </tbody>
              </table>
            </div>
          `}
        </div>
      </div>

      <p class="text-center text-xs text-base-content/50">
        Read-only public view. Sign in to access counter mode and coach features.
      </p>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'public-game-view': PublicGameView;
  }
}
